import { useState } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ShareMenu } from "@/components/ShareMenu";
import { SampleReader } from "@/components/SampleReader";
import { BOOKS, AUTHOR, STORE_URL, COLLECTION_TOTAL } from "@/lib/books";
import { motion } from "framer-motion";
import { ShoppingBag, ExternalLink, Truck, BookOpen, Library as LibraryIcon } from "lucide-react";

const FADE = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.5 } };

const featured = BOOKS.find((b) => b.featured);
const others = BOOKS.filter((b) => !b.featured);

const pageUrl = (slug) => (typeof window !== "undefined" ? `${window.location.origin}/library#${slug}` : `/library#${slug}`);

function BookActions({ book, onSample, className = "" }) {
  return (
    <div className={`flex flex-wrap items-center gap-4 ${className}`}>
      <a href={book.url} target="_blank" rel="noopener noreferrer" data-testid={`buy-btn-${book.slug}`}>
        <Button className="rounded-none bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#C8102E] hover:text-white px-7 py-6">
          <ShoppingBag className="mr-2 w-4 h-4" /> Purchase · {book.price}
        </Button>
      </a>
      <Button
        variant="outline"
        onClick={() => onSample(book)}
        data-testid={`sample-btn-${book.slug}`}
        className="rounded-none bg-transparent border-[#D4AF37]/50 text-[#F5F3EF] hover:bg-[#D4AF37] hover:text-[#0A0A0A] px-6 py-6"
      >
        <BookOpen className="mr-2 w-4 h-4" /> Read a Sample
      </Button>
      <ShareMenu url={pageUrl(book.slug)} text={`${book.title} by ${book.author}`} testId={`share-${book.slug}`} />
    </div>
  );
}

export default function Library() {
  const [sampleBook, setSampleBook] = useState(null);

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      <div className="tribal-stripe h-1.5 w-full" />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 pt-20 pb-12">
        <div className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold mb-5">Publications</div>
        <h1 className="font-serif-display text-5xl sm:text-6xl font-semibold tracking-tight leading-[0.98] text-[#F5F3EF]">The Library</h1>
        <p className="mt-7 text-lg text-[#A8A29E] leading-relaxed max-w-2xl">
          Scholarship in service of <span className="text-[#F5F3EF] font-medium">Truth, Justice, and Legacy</span>. Works published
          through {AUTHOR.publisher} that examine our history, our law, and the codes by which we build strong individuals,
          families, and communities.
        </p>
      </section>

      {/* Featured book */}
      <section id={featured.slug} className="border-y border-[#D4AF37]/20 bg-black scroll-mt-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 py-20 grid lg:grid-cols-5 gap-12 lg:gap-16 items-center">
          <motion.div {...FADE} className="lg:col-span-2 flex justify-center lg:justify-start">
            <img
              src={featured.cover}
              alt={`${featured.title} — front cover`}
              className="w-64 sm:w-80 shadow-2xl shadow-black/70 border border-[#D4AF37]/30"
              data-testid="featured-book-cover"
            />
          </motion.div>
          <motion.div {...FADE} className="lg:col-span-3" data-testid="featured-book">
            <div className="text-xs tracking-[0.25em] uppercase text-[#C8102E] font-semibold mb-3">New Release · Featured</div>
            <div className="text-sm text-[#A8A29E] italic mb-2">{featured.subtitle}</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl text-[#F5F3EF] tracking-tight leading-tight">{featured.title}</h2>
            <div className="text-[#D4AF37] mt-3 font-medium">by {featured.author}</div>
            <p className="text-[#A8A29E] leading-relaxed mt-6">{featured.blurb}</p>
            <div className="text-sm text-[#A8A29E] mt-5">{featured.format} · {featured.price}</div>
            <BookActions book={featured} onSample={setSampleBook} className="mt-6" />
          </motion.div>
        </div>
      </section>

      {/* Other titles */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 py-20">
        <div className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold mb-3">The Collection</div>
        <h2 className="font-serif-display text-4xl sm:text-5xl text-[#F5F3EF] tracking-tight mb-12">More Works</h2>
        <div className="grid md:grid-cols-2 gap-8">
          {others.map((b) => (
            <motion.article key={b.slug} id={b.slug} {...FADE} className="bg-[#141414] border border-[#D4AF37]/30 p-7 sm:p-8 flex flex-col sm:flex-row gap-7 scroll-mt-24" data-testid={`book-card-${b.slug}`}>
              <img src={b.cover} alt={`${b.title} — front cover`} className="w-40 shrink-0 self-start shadow-xl shadow-black/60 border border-[#D4AF37]/20" />
              <div className="flex flex-col">
                <div className="text-xs text-[#A8A29E] italic mb-1">{b.subtitle}</div>
                <h3 className="font-serif-display text-2xl sm:text-3xl text-[#F5F3EF] leading-tight">{b.title}</h3>
                <div className="text-[#D4AF37] text-sm mt-2">by {b.author}</div>
                <p className="text-sm text-[#A8A29E] leading-relaxed mt-4 flex-1">{b.blurb}</p>
                <div className="text-xs text-[#A8A29E] mt-4">{b.format} · {b.price}</div>
                <BookActions book={b} onSample={setSampleBook} className="mt-4" />
              </div>
            </motion.article>
          ))}
        </div>
        <div className="mt-10 flex flex-wrap items-center gap-6 text-sm text-[#A8A29E]">
          <span className="flex items-center gap-2"><Truck className="w-4 h-4 text-[#5DBB7A]" /> Paperback editions with worldwide shipping</span>
          <a href={STORE_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-[#D4AF37] hover:text-[#F5F3EF] transition-colors" data-testid="store-link">
            Visit the full store on Books.by <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </section>

      {/* Complete Collection */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 pb-20">
        <motion.div {...FADE} className="relative overflow-hidden border border-[#D4AF37]/40 bg-[#141414] p-8 sm:p-12 grid lg:grid-cols-5 gap-10 items-center" data-testid="collection-callout">
          <div className="absolute inset-y-0 left-0 w-1.5 tribal-stripe-vertical" />
          <div className="lg:col-span-2 flex justify-center">
            <div className="flex items-end -space-x-10">
              {BOOKS.map((b, i) => (
                <img
                  key={b.slug}
                  src={b.cover}
                  alt={b.title}
                  className="w-28 sm:w-36 shadow-2xl shadow-black/80 border border-[#D4AF37]/30"
                  style={{ transform: `rotate(${(i - 1) * 6}deg) translateY(${Math.abs(i - 1) * 8}px)`, zIndex: i === 1 ? 2 : 1 }}
                />
              ))}
            </div>
          </div>
          <div className="lg:col-span-3">
            <div className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold mb-3 flex items-center gap-2"><LibraryIcon className="w-4 h-4" /> Complete Collection</div>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-[#F5F3EF] tracking-tight leading-tight">Own all three titles.</h2>
            <p className="text-[#A8A29E] mt-4 leading-relaxed max-w-xl">
              History, law, and the codes of righteous living — together they form a single body of work. Add all three
              paperbacks to your cart on Books.by and build the foundation of your family's library.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-5">
              <a href={STORE_URL} target="_blank" rel="noopener noreferrer" data-testid="collection-buy-btn">
                <Button className="rounded-none bg-[#C8102E] text-white hover:bg-[#D4AF37] hover:text-[#0A0A0A] px-8 py-6">
                  <ShoppingBag className="mr-2 w-4 h-4" /> Get the Collection · {COLLECTION_TOTAL}
                </Button>
              </a>
              <span className="text-xs text-[#A8A29E]" data-testid="collection-total-note">{BOOKS.length} paperbacks · combined list price</span>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Author */}
      <section className="border-y border-[#D4AF37]/20 bg-black">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 py-20 grid lg:grid-cols-3 gap-12 items-start" data-testid="author-section">
          <div>
            <img src={AUTHOR.photo} alt={AUTHOR.name} className="w-56 h-56 object-cover border border-[#D4AF37]/40 shadow-2xl shadow-black/70" />
            <div className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold mt-8 mb-2">About the Author</div>
            <h2 className="font-serif-display text-3xl text-[#F5F3EF]">{AUTHOR.name}</h2>
            <div className="text-[#A8A29E] text-sm mt-1">writing as {AUTHOR.penName}</div>
            <div className="text-[#D4AF37] text-sm mt-3 flex items-center gap-2"><BookOpen className="w-4 h-4" /> Founder, {AUTHOR.publisher}</div>
          </div>
          <div className="lg:col-span-2 space-y-5 text-[#A8A29E] leading-relaxed">
            {AUTHOR.bio.map((p) => <p key={p.slice(0, 40)}>{p}</p>)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 py-20 text-center">
        <h2 className="font-serif-display text-3xl sm:text-4xl text-[#F5F3EF] tracking-tight max-w-2xl mx-auto">Build your library. Strengthen the record of our people.</h2>
        <a href={STORE_URL} target="_blank" rel="noopener noreferrer" data-testid="cta-store-btn">
          <Button className="mt-8 rounded-none bg-[#C8102E] text-white hover:bg-[#D4AF37] hover:text-[#0A0A0A] px-10 py-6 text-base">
            Shop All Titles <ExternalLink className="ml-2 w-4 h-4" />
          </Button>
        </a>
        <p className="text-xs text-[#A8A29E]/70 mt-6">Purchases are completed securely on Books.by. Titles published by {AUTHOR.publisher}.</p>
      </section>

      <SampleReader book={sampleBook} open={!!sampleBook} onOpenChange={(o) => !o && setSampleBook(null)} />
      <Footer />
    </div>
  );
}
